import { Badge, Col, Row } from 'react-bootstrap';

// Presentational "Our Team" content — renders a resolved team profile
// (GET /api/team-profiles/:scope shape). Used by the homepage popup and, copied, by the admin
// panel's live preview. No data fetching here.
//
// `apiBase` is prefixed to a stored /uploads/... photo path.

function photoUrl(path, apiBase) {
  if (!path) return '';
  if (/^https?:\/\//.test(path)) return path;
  if (path.startsWith('/uploads/')) return `${apiBase || ''}${path}`;
  return path;
}

function initialsOf(name) {
  return (name || '?').split(/\s+/).map((w) => w.charAt(0)).slice(0, 2).join('').toUpperCase();
}

function Avatar({ name, photoUrl: url }) {
  if (url) return <img className="rv-team-photo" src={url} alt={name} loading="lazy" />;
  return <div className="rv-team-photo rv-team-photo--empty" aria-hidden="true">{initialsOf(name)}</div>;
}

export default function TeamContent({ data, apiBase = '' }) {
  if (!data) return null;
  const {
    orgName, intro, story = [], principles = [], functions = [], members = [],
  } = data;

  const funcLabel = (key) => functions.find((f) => f.key === key)?.label || key;

  const execs = members.filter((m) => m.seniority === 'exec');
  const leads = members.filter((m) => m.seniority === 'lead');
  // Everyone else, grouped by their primary function (functionKeys[0]).
  const rest = members.filter((m) => m.seniority !== 'exec' && m.seniority !== 'lead');
  const groups = functions
    .map((f) => ({ ...f, people: rest.filter((m) => (m.functionKeys || [])[0] === f.key) }))
    .filter((g) => g.people.length > 0);
  const ungrouped = rest.filter((m) => !functions.some((f) => f.key === (m.functionKeys || [])[0]));
  if (ungrouped.length) groups.push({ key: '_other', label: 'Team', description: '', people: ungrouped });

  return (
    <div className="rv-team">
      {(orgName || intro) && (
        <div className="mb-4">
          {orgName && <h4 className="mb-1">{orgName}</h4>}
          {intro && <p className="text-muted mb-0">{intro}</p>}
        </div>
      )}

      {story.length > 0 && (
        <section className="mb-4">
          <h5 className="mb-2">How we came together</h5>
          {story.map((para, i) => (
            <p key={i} className={i === story.length - 1 ? 'mb-0' : undefined}>{para}</p>
          ))}
        </section>
      )}

      {principles.length > 0 && (
        <section className="mb-4">
          <h5 className="mb-2">How we build and innovate</h5>
          <Row xs={1} md={2} className="g-3">
            {principles.map((p, i) => (
              <Col key={i}>
                <div className="rv-team-principle h-100">
                  {p.title && <div className="fw-semibold mb-1">{p.title}</div>}
                  {p.body && <div className="small text-muted">{p.body}</div>}
                </div>
              </Col>
            ))}
          </Row>
        </section>
      )}

      {(execs.length > 0 || leads.length > 0) && (
        <section className="mb-4">
          <h5 className="mb-1">Roles &amp; hierarchy</h5>
          <p className="text-muted small mb-3">
            Each function owns its area end to end. Some people work across more than one.
          </p>
          <div className="rv-team-org">
            {execs.length > 0 && (
              <>
                <Row xs={1} sm={execs.length > 1 ? 2 : 1} className="g-3 justify-content-center">
                  {execs.map((m) => (
                    <Col key={m.name} className={execs.length === 1 ? 'col-sm-6' : undefined}>
                      <div className="rv-team-card rv-team-card--exec text-center">
                        <Avatar name={m.name} photoUrl={photoUrl(m.photoUrl, apiBase)} />
                        <div className="fw-semibold">{m.name}</div>
                        <div className="small text-muted">{m.title}</div>
                        {m.bio && <div className="small mt-1">{m.bio}</div>}
                      </div>
                    </Col>
                  ))}
                </Row>
                {leads.length > 0 && <div className="rv-team-connector" aria-hidden="true" />}
              </>
            )}
            <Row xs={1} sm={2} lg={3} className="g-3">
              {leads.map((m) => (
                <Col key={m.name}>
                  <div className="rv-team-card text-start h-100">
                    <div className="d-flex align-items-center gap-2 mb-2">
                      <Avatar name={m.name} photoUrl={photoUrl(m.photoUrl, apiBase)} />
                      <div>
                        <div className="fw-semibold">{m.name}</div>
                        <div className="small text-muted">{m.title}</div>
                      </div>
                    </div>
                    {(m.functionKeys || []).length > 0 && (
                      <div className="d-flex flex-wrap gap-1 mb-2">
                        {m.functionKeys.map((k) => <Badge key={k} bg="secondary">{funcLabel(k)}</Badge>)}
                      </div>
                    )}
                    {(m.expertise || []).length > 0 && (
                      <ul className="rv-team-covers">
                        {m.expertise.map((e) => <li key={e}>{e}</li>)}
                      </ul>
                    )}
                  </div>
                </Col>
              ))}
            </Row>
          </div>
        </section>
      )}

      {groups.length > 0 && (
        <section>
          <h5 className="mb-3">The people</h5>
          {groups.map((g) => (
            <div key={g.key} className="mb-4">
              <div className="d-flex align-items-center gap-2 flex-wrap mb-1">
                <strong>{g.label}</strong>
                <Badge bg="secondary">{g.people.length}</Badge>
              </div>
              {g.description && <p className="text-muted small mb-2">{g.description}</p>}
              <Row xs={1} sm={2} md={3} className="g-3">
                {g.people.map((m) => (
                  <Col key={m.name}>
                    <div className="rv-team-card text-center h-100">
                      <Avatar name={m.name} photoUrl={photoUrl(m.photoUrl, apiBase)} />
                      <div className="fw-semibold">{m.name}</div>
                      <div className="small text-muted">{m.title}</div>
                      {(m.functionKeys || []).length > 1 && (
                        <div className="d-flex flex-wrap gap-1 justify-content-center mt-1">
                          {m.functionKeys.map((k) => (
                            <Badge key={k} bg="light" text="dark" className="border">{funcLabel(k)}</Badge>
                          ))}
                        </div>
                      )}
                      {m.bio && <div className="small mt-1">{m.bio}</div>}
                    </div>
                  </Col>
                ))}
              </Row>
            </div>
          ))}
        </section>
      )}
    </div>
  );
}
